/**
 * Full-width row in the Studio's tool list — "Extend Song", "Remix", and so on.
 *
 * Replaces the monolith's `studioToolBtn(c)` style helper. Styles verbatim. The
 * Vault's Favorites and Recent lists spread the same helper without an icon, so
 * `icon` is optional and lives here rather than in a near-duplicate component.
 */
import type { ReactNode } from 'react';
import { c, DISABLED_OPACITY } from '../../styles/theme';

export interface ToolButtonProps {
  /** Leading icon. Omitted by the Vault's plain text rows. */
  icon?: ReactNode;
  children: ReactNode;
  onClick: () => void;
  /** Right-aligned content, e.g. the credit cost. */
  trailing?: ReactNode;
  disabled?: boolean;
}

export default function ToolButton({
  icon,
  children,
  onClick,
  trailing,
  disabled,
}: ToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      /*
        THE LAYOUT IS IN CSS (.lyx-tool-button), the colours are here. Split for the
        reason .lyx-tool-panel and .lyx-tool-slider state: an inline style prop outranks
        a media query, and this row has to wrap on a phone. Split Stems renders five of
        these ("Vocals.wav" + a Preview pill + a download glyph) and the Download Center
        five more; with `display: flex` inline there was no way to give the row
        `flex-wrap` at a breakpoint, so at 380px the label, the pill and the trailing
        glyph stayed on one line and the pill was pushed past the button's own border.
      */
      className="lyx-tool-button"
      style={{
        border: `1px solid ${c.border}`,
        background: c.surface2,
        color: c.text1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        // Shared with Button/IconButton; see styles/theme.ts.
        opacity: disabled ? DISABLED_OPACITY : 1,
      }}
    >
      {icon}
      {children}
      {trailing && <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{trailing}</span>}
    </button>
  );
}
